import { useEffect, useRef, useState, type ReactNode } from 'react'

// A flag drawn and animated entirely in a fragment shader: stripes, canton and
// all fifty stars come from the official proportions (4 U.S.C. 1, Executive
// Order 10834), and the cloth ripples with simple wave displacement and
// shading. Nothing is downloaded and nothing needs a license.

const VERTEX = `
attribute vec2 position;
varying vec2 vUv;
void main() {
  vUv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}`

const FRAGMENT = `
precision highp float;
varying vec2 vUv;
uniform float uTime;
uniform vec2 uResolution;
uniform vec3 uRed;
uniform vec3 uBlue;
uniform vec2 uPointer;
uniform float uPush;
// 0 lays the cloth flat; 1 is the full wave.
uniform float uWave;

// Inigo Quilez's signed distance to a five-pointed star.
float sdStar5(vec2 p, float r, float rf) {
  const vec2 k1 = vec2(0.809016994375, -0.587785252292);
  const vec2 k2 = vec2(-k1.x, k1.y);
  p.x = abs(p.x);
  p -= 2.0 * max(dot(k1, p), 0.0) * k1;
  p -= 2.0 * max(dot(k2, p), 0.0) * k2;
  p.x = abs(p.x);
  p.y -= r;
  vec2 ba = rf * vec2(-k1.y, k1.x) - vec2(0.0, 1.0);
  float h = clamp(dot(p, ba) / dot(ba, ba), 0.0, r);
  return length(p - ba * h) * sign(p.y * ba.x - p.x * ba.y);
}

void main() {
  // Cover the canvas with flag cloth at the official 1.9 : 1 ratio, cropped.
  float aspect = uResolution.x / uResolution.y;
  vec2 flag;
  if (aspect >= 1.9) {
    float h = 1.9 / aspect;
    flag = vec2(vUv.x * 1.9, (1.0 - vUv.y) * h + (1.0 - h) * 0.5);
  } else {
    flag = vec2(vUv.x * aspect, 1.0 - vUv.y);
  }

  // Waves travel from the hoist; amplitude grows toward the fly.
  float phase = flag.x * 4.2 - uTime * 1.6;
  float amp = 0.035 * (0.25 + flag.x * 0.45) * uWave;
  vec2 p = flag + vec2(0.0, sin(phase) * amp + sin(phase * 0.53 + 1.3) * amp * 0.6);
  // The pointer sends a ripple across the cloth.
  float reach = distance(flag, uPointer);
  float ripple = uPush * uWave * 0.05 * sin(reach * 26.0 - uTime * 7.0) * exp(-reach * 3.2);
  p.y += ripple;
  float shade = 1.0 + uWave * (0.18 * cos(phase) + 0.06 * cos(phase * 0.53 + 1.3) - 0.18) + ripple * 3.0;

  vec3 white = vec3(1.0);
  float stripe = floor(clamp(p.y, 0.0, 0.9999) * 13.0);
  vec3 color = mod(stripe, 2.0) < 0.5 ? uRed : white;

  // Canton: 0.76 wide, seven stripes tall.
  if (p.x < 0.76 && p.y < 7.0 / 13.0) {
    color = uBlue;
    float d = 1.0;
    for (int row = 0; row < 9; row++) {
      float even = mod(float(row), 2.0) < 0.5 ? 1.0 : 0.0;
      float y = 0.054 + float(row) * 0.054;
      for (int col = 0; col < 6; col++) {
        if (even < 0.5 && col > 4) break;
        float x = 0.063 + (1.0 - even) * 0.063 + float(col) * 0.126;
        d = min(d, sdStar5(vec2(p.x - x, -(p.y - y)), 0.0308, 0.382));
      }
    }
    float edge = fwidth(d) + 0.0008;
    color = mix(white, color, smoothstep(-edge, edge, d));
  }

  gl_FragColor = vec4(color * shade, 1.0);
}`

function compile(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type)!
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  return gl.getShaderParameter(shader, gl.COMPILE_STATUS) ? shader : null
}

type WavingFlagProps = {
  /** Stop on the current frame. */
  paused?: boolean
  className?: string
  red?: string
  blue?: string
  /** Ripple the cloth where the pointer moves. */
  interactive?: boolean
  /** Lay the cloth flat while paused, easing into and out of the wave. */
  flatWhenPaused?: boolean
  /** Shown when WebGL is unavailable or the browser reclaims the context. */
  fallback?: ReactNode
}

const toVec3 = (hex: string) =>
  [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as [number, number, number]

/** A full-bleed waving flag, or the fallback where WebGL is unavailable. */
export default function WavingFlag({
  paused = false,
  className,
  red = '#b31942',
  blue = '#0a3161',
  interactive = false,
  flatWhenPaused = false,
  fallback = null,
}: WavingFlagProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)
  const pausedRef = useRef(paused)
  useEffect(() => {
    pausedRef.current = paused
  }, [paused])

  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    // A fresh canvas each time: a canvas whose context was released on unmount
    // (as development remounts do) only ever hands back that lost context.
    const canvas = document.createElement('canvas')
    canvas.setAttribute('aria-hidden', 'true')
    if (className) canvas.className = className
    host.appendChild(canvas)
    const gl = canvas.getContext('webgl', { antialias: true, premultipliedAlpha: false })
    // Browsers cap live WebGL contexts per page and reclaim the oldest.
    const lost = () => setFailed(true)
    canvas.addEventListener('webglcontextlost', lost)
    if (!gl) {
      lost()
      canvas.remove()
      return
    }
    gl.getExtension('OES_standard_derivatives')
    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX)
    const fragment = compile(gl, gl.FRAGMENT_SHADER, `#extension GL_OES_standard_derivatives : enable\n${FRAGMENT}`)
    if (!vertex || !fragment) return () => canvas.remove()
    const program = gl.createProgram()!
    gl.attachShader(program, vertex)
    gl.attachShader(program, fragment)
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return () => canvas.remove()
    gl.useProgram(program)

    const buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)
    const position = gl.getAttribLocation(program, 'position')
    gl.enableVertexAttribArray(position)
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)

    const uTime = gl.getUniformLocation(program, 'uTime')
    const uPointer = gl.getUniformLocation(program, 'uPointer')
    const uPush = gl.getUniformLocation(program, 'uPush')
    const uWave = gl.getUniformLocation(program, 'uWave')
    let wave = flatWhenPaused ? 0 : 1
    const pointer = { x: -10, y: -10, push: 0 }
    const toFlag = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      const u = (event.clientX - rect.left) / rect.width
      const v = (event.clientY - rect.top) / rect.height
      const aspect = rect.width / rect.height
      if (aspect >= 1.9) {
        const h = 1.9 / aspect
        pointer.x = u * 1.9
        pointer.y = v * h + (1 - h) * 0.5
      } else {
        pointer.x = u * aspect
        pointer.y = v
      }
      pointer.push = 1
    }
    if (interactive) canvas.addEventListener('pointermove', toFlag)
    const uResolution = gl.getUniformLocation(program, 'uResolution')
    gl.uniform3fv(gl.getUniformLocation(program, 'uRed'), toVec3(red))
    gl.uniform3fv(gl.getUniformLocation(program, 'uBlue'), toVec3(blue))

    let frame = 0
    let visible = true
    let elapsed = 0
    let last = performance.now()

    const resize = () => {
      const scale = Math.min(window.devicePixelRatio || 1, 1.5)
      canvas.width = Math.max(1, Math.round(canvas.clientWidth * scale))
      canvas.height = Math.max(1, Math.round(canvas.clientHeight * scale))
      gl.viewport(0, 0, canvas.width, canvas.height)
      gl.uniform2f(uResolution, canvas.width, canvas.height)
    }

    const draw = (now: number) => {
      frame = 0
      const step = now - last
      last = now
      // A flag that lies flat keeps its time running while it settles.
      if (!pausedRef.current || (flatWhenPaused && wave > 0.01)) elapsed += step / 1000
      pointer.push *= 0.97
      gl.uniform1f(uTime, elapsed)
      gl.uniform2f(uPointer, pointer.x, pointer.y)
      gl.uniform1f(uPush, pointer.push)
      if (flatWhenPaused) {
        // Eases over about half a second, whatever the frame rate.
        wave += ((pausedRef.current ? 0 : 1) - wave) * (1 - Math.exp(-step / 160))
        if (pausedRef.current && wave < 0.002) wave = 0
      }
      gl.uniform1f(uWave, wave)
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
      if (visible) frame = requestAnimationFrame(draw)
    }

    const observer = new ResizeObserver(resize)
    observer.observe(canvas)
    const visibility = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
      if (visible && !frame) {
        last = performance.now()
        frame = requestAnimationFrame(draw)
      }
    })
    visibility.observe(canvas)
    resize()
    frame = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      visibility.disconnect()
      canvas.removeEventListener('pointermove', toFlag)
      canvas.removeEventListener('webglcontextlost', lost)
      gl.getExtension('WEBGL_lose_context')?.loseContext()
      canvas.remove()
    }
  }, [red, blue, interactive, flatWhenPaused, className])

  if (failed) return <>{fallback}</>
  return <div ref={hostRef} className="mds-waving-flag" aria-hidden="true" />
}
